/**
 * Gateway scope picker (Part 1) — grant a key its API scopes from the customer vocabulary.
 *
 * Loads the grantable `<resource>:<action>` vocabulary (Slice D) grouped by module, lets the
 * operator check the scopes a key may use, and persists via PUT /keys/{id}/scopes (Slice D-2).
 * Empty selection clears all scopes (no API-scope filter). The vocabulary is memoized across
 * opens — it's the same platform-wide list for every key.
 *
 * Scopes are not enforced at the gateway yet (that's a later slice); this manages the grant.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { KeyRound } from 'lucide-react';
import { Button } from './Button';
import { Pill } from './Pill';
import { Card } from './Card';
import * as api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const unwrap = (res) => (res && res.data ? res.data : res);

// Process-lifetime cache — the vocabulary is identical for every key.
let _vocabCache = null;

export function GatewayScopePicker({ keyId, value, onSaved }) {
  const { addToast } = useToast();
  const [vocab, setVocab] = useState(_vocabCache || []);
  const [loading, setLoading] = useState(!_vocabCache);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set(Array.isArray(value) ? value : []));

  // Re-seed selection whenever the key (or its current grants) changes.
  useEffect(() => {
    setSelected(new Set(Array.isArray(value) ? value : []));
  }, [keyId, value]);

  useEffect(() => {
    if (_vocabCache) { setVocab(_vocabCache); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = unwrap(await api.gatewayGetAvailableScopes()) || [];
        const arr = Array.isArray(list) ? list : [];
        if (!cancelled) { _vocabCache = arr; setVocab(arr); }
      } catch (e) {
        if (!cancelled) addToast({ tone: 'danger', message: e?.message || 'Failed to load scopes' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [addToast]);

  // Group descriptors by module label for the picker.
  const groups = useMemo(() => {
    const m = {};
    vocab.forEach((d) => {
      const g = d.moduleLabel || d.module || 'Other';
      (m[g] = m[g] || []).push(d);
    });
    Object.values(m).forEach((rows) => rows.sort((a, b) => String(a.scope).localeCompare(String(b.scope))));
    return Object.keys(m).sort().map((k) => ({ label: k, rows: m[k] }));
  }, [vocab]);

  const toggle = (scope) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(scope)) next.delete(scope); else next.add(scope);
    return next;
  });

  const toggleGroup = (rows, on) => setSelected((prev) => {
    const next = new Set(prev);
    rows.forEach((d) => { if (on) next.add(d.scope); else next.delete(d.scope); });
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.gatewayUpdateApiScopes(keyId, [...selected]);
      if (res && res.ok === false) throw new Error(res.error || 'Save failed');
      addToast({ tone: 'success', message: `Scopes updated (${selected.size})` });
      if (onSaved) onSaved(unwrap(res));
    } catch (e) {
      addToast({ tone: 'danger', message: e?.message || 'Failed to update scopes' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card padding="sm">
      <Card.Body>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <KeyRound size={13} className="text-v2-action-strong" />
            <div className="text-[12px] font-semibold text-v2-ink">API scopes</div>
          </div>
          <Pill tone={selected.size ? 'action' : 'muted'} size="sm">{selected.size} selected</Pill>
        </div>
        <div className="text-[11px] text-v2-ink-muted mb-2">
          The <span className="font-mono">resource:action</span> permissions this key may use. None selected =
          no scope filter. Grants are managed here; enforcement is applied by the gateway.
        </div>

        {loading && <div className="text-v2-ink-muted text-[12px]">Loading scopes…</div>}

        {!loading && groups.length === 0 && (
          <div className="text-[12px] text-v2-ink-muted border border-v2-border-subtle rounded-v2-sm px-3 py-3">
            No customer-reachable scopes are published yet.
          </div>
        )}

        {!loading && groups.map((g) => {
          const allOn = g.rows.every((d) => selected.has(d.scope));
          return (
            <div key={g.label} className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[11px] font-semibold text-v2-ink">{g.label}</div>
                <button type="button"
                  className="text-[10.5px] text-v2-action hover:underline"
                  onClick={() => toggleGroup(g.rows, !allOn)}>
                  {allOn ? 'Clear all' : 'Select all'}
                </button>
              </div>
              <div className="space-y-1">
                {g.rows.map((d) => (
                  <label key={d.scope} className="flex items-center gap-2 text-[12.5px] text-v2-ink cursor-pointer">
                    <input type="checkbox" checked={selected.has(d.scope)} onChange={() => toggle(d.scope)} />
                    <span className="flex-1">{d.label || d.scope}</span>
                    <span className="font-mono text-[11px] text-v2-ink-muted">{d.scope}</span>
                    {Array.isArray(d.methods) && d.methods.length > 0 && (
                      <Pill tone="muted" size="sm">{d.methods.join(' ')}</Pill>
                    )}
                  </label>
                ))}
              </div>
            </div>
          );
        })}

        {!loading && (
          <div className="flex items-center gap-2 mt-1">
            <Button variant="primary" size="sm" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save scopes'}
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default GatewayScopePicker;
